import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, User, ShieldCheck, Ban, ChevronRight, CheckCircle2, Star } from "lucide-react";

function fmtINR(n) {
    return `₹${(Number(n || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function EnrollmentsAndFeedback({ userId, orders }) {
    const [completions, setCompletions] = useState([]);
    const [feedbacks, setFeedbacks] = useState([]);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        const [c, f] = await Promise.all([
            api.get(`/admin/enrollments?user_id=${userId}`).then((r) => r.data).catch(() => []),
            api.get(`/admin/feedbacks?user_id=${userId}`).then((r) => r.data).catch(() => []),
        ]);
        setCompletions(c || []);
        setFeedbacks(f || []);
    };
    useEffect(() => { reload(); /* eslint-disable-line */ }, [userId]);

    // Build a list of (order, course) pairs for course items in paid orders.
    const courseRows = [];
    for (const o of (orders || [])) {
        if (!["paid", "shipped", "delivered"].includes(o.status)) continue;
        for (const it of (o.items || [])) {
            if (it.kind !== "course") continue;
            const existing = completions.find((c) => c.order_id === o.id && c.course_id === it.id);
            courseRows.push({ order: o, item: it, completion: existing || null });
        }
    }

    const markComplete = async (order_id, course_id) => {
        setBusy(true);
        try {
            await api.post("/admin/enrollments/mark-complete", { user_id: userId, order_id, course_id });
            toast.success("Marked complete — user will be prompted for feedback");
            await reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    const fbById = Object.fromEntries(feedbacks.map((f) => [f.id, f]));

    return (
        <div data-testid="customer-enrollments">
            <h4 className="font-semibold mb-2">Enrollments & feedback</h4>
            {courseRows.length === 0 ? (
                <div className="rounded-2xl bg-paper-alt border border-dashed border-gray-200 p-4 text-sm text-ink-700">No paid course enrollments yet.</div>
            ) : (
                <div className="space-y-2">
                    {courseRows.map(({ order, item, completion }) => {
                        const fb = completion?.feedback_id ? fbById[completion.feedback_id] : null;
                        return (
                            <div key={`${order.id}-${item.id}`} className="rounded-2xl bg-white border border-gray-100 p-3.5 text-sm" data-testid={`enrollment-row-${order.id}-${item.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="font-semibold text-ink-900 truncate">{item.name}</div>
                                        <div className="text-[11px] text-ink-400 mt-0.5">
                                            Order <span className="font-mono">#{order.id.slice(0, 8)}</span> · {String(order.created_at).slice(0, 10)} · mode {item.mode || "online"}
                                        </div>
                                    </div>
                                    <div>
                                        {!completion ? (
                                            <Button
                                                size="sm"
                                                disabled={busy}
                                                onClick={() => markComplete(order.id, item.id)}
                                                className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta h-8 px-3 text-xs"
                                                data-testid={`mark-complete-${order.id}-${item.id}`}
                                            >
                                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark complete
                                            </Button>
                                        ) : completion.status === "feedback_pending" ? (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 text-[11px] font-semibold uppercase tracking-wide">Awaiting feedback</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-[11px] font-semibold uppercase tracking-wide">
                                                <CheckCircle2 className="h-3 w-3" /> Completed
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {fb && (
                                    <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`feedback-summary-${fb.id}`}>
                                        <div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold mb-2 flex items-center gap-1.5">
                                            <Star className="h-3 w-3 text-accent-500" /> Feedback submitted {new Date(fb.created_at).toLocaleString()}
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                                            {[
                                                ["Overall", fb.overall_satisfaction],
                                                ["Instructor", fb.instructor_expertise],
                                                ["Facility", fb.facility_ambiance],
                                                ["Enrollment", fb.enrollment_process],
                                                ["Expectations", fb.program_expectations],
                                                ["Revisit", fb.revisit_intentions],
                                                ["Recommend", fb.recommendation],
                                            ].map(([k, v]) => (
                                                <div key={k} className="bg-paper-alt rounded-lg px-2 py-1.5">
                                                    <div className="text-ink-700">{k}</div>
                                                    <div className="font-semibold text-ink-900 text-sm">{v}/5</div>
                                                </div>
                                            ))}
                                        </div>
                                        {Object.keys(fb.block_ratings || {}).length > 0 && (
                                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                                                {Object.entries(fb.block_ratings).map(([k, v]) => (
                                                    <div key={k} className="bg-brand-50 rounded-lg px-2 py-1.5">
                                                        <div className="text-ink-700 capitalize">{k.replace(/_/g, " ")}</div>
                                                        <div className="font-semibold text-ink-900 text-sm">{v}/5</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {fb.additional_comments && (
                                            <div className="mt-2 text-xs text-ink-900 bg-paper-alt rounded-lg px-3 py-2 whitespace-pre-line">{fb.additional_comments}</div>
                                        )}
                                        <div className="text-[10px] text-ink-400 mt-1.5">Program: {fb.program_category?.replace(/_/g, " ")} — {fb.program_name}</div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function CustomerDetail({ open, onOpenChange, userId, onSaved }) {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && userId) {
            setData(null);
            api.get(`/admin/customers/${userId}`).then(({ data }) => setData(data)).catch(() => setData(null));
        }
    }, [open, userId]);

    const updateField = async (patch) => {
        setSaving(true);
        try {
            const { data: u } = await api.patch(`/admin/customers/${userId}`, patch);
            setData((d) => ({ ...d, ...u }));
            toast.success("Customer updated");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-3xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Customer detail</DialogTitle>
                </DialogHeader>
                {!data ? (
                    <div className="text-center py-10 text-ink-700">Loading…</div>
                ) : (
                    <div className="space-y-5">
                        <div className="rounded-2xl bg-paper-alt p-4">
                            <div className="flex flex-wrap items-center gap-3 justify-between">
                                <div>
                                    <div className="text-xl font-semibold">{data.name || "—"}</div>
                                    <div className="text-sm text-ink-700">{data.email}</div>
                                    <div className="text-xs text-ink-700 mt-1">Joined {String(data.created_at || "").slice(0, 10)}</div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {data.role === "admin"
                                        ? <Button size="sm" variant="outline" className="rounded-full" disabled={saving} onClick={() => updateField({ role: "user" })} data-testid="customer-demote-btn">Revoke admin</Button>
                                        : <Button size="sm" variant="outline" className="rounded-full" disabled={saving} onClick={() => updateField({ role: "admin" })} data-testid="customer-promote-btn"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Make admin</Button>}
                                    {data.is_suspended
                                        ? <Button size="sm" variant="outline" className="rounded-full" disabled={saving} onClick={() => updateField({ is_suspended: false })} data-testid="customer-unsuspend-btn">Reactivate</Button>
                                        : <Button size="sm" variant="outline" className="rounded-full text-accent-700 hover:bg-accent-50" disabled={saving} onClick={() => updateField({ is_suspended: true })} data-testid="customer-suspend-btn"><Ban className="h-3.5 w-3.5 mr-1" /> Suspend</Button>}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-2xl bg-white border border-gray-100 p-3 text-center">
                                <div className="text-xs uppercase tracking-wide text-ink-700">Orders</div>
                                <div className="text-2xl font-heading font-semibold">{data.orders_count}</div>
                            </div>
                            <div className="rounded-2xl bg-white border border-gray-100 p-3 text-center">
                                <div className="text-xs uppercase tracking-wide text-ink-700">Lifetime</div>
                                <div className="text-2xl font-heading font-semibold">{fmtINR(data.lifetime_value)}</div>
                            </div>
                            <div className="rounded-2xl bg-white border border-gray-100 p-3 text-center">
                                <div className="text-xs uppercase tracking-wide text-ink-700">Status</div>
                                <div className={`text-sm font-semibold mt-2 ${data.is_suspended ? "text-accent-700" : "text-brand-600"}`}>{data.is_suspended ? "Suspended" : "Active"}</div>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-2">Enrollment profile</h4>
                            {data.profile ? (
                                <div className="rounded-2xl bg-white border border-gray-100 p-4 text-sm space-y-3" data-testid="customer-profile-section">
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        <div><div className="text-[11px] uppercase text-ink-400">Name</div><div className="font-medium">{[data.profile.first_name, data.profile.middle_name, data.profile.last_name].filter(Boolean).join(" ")}</div></div>
                                        <div><div className="text-[11px] uppercase text-ink-400">Date of birth</div><div className="font-medium">{data.profile.date_of_birth}</div></div>
                                        <div><div className="text-[11px] uppercase text-ink-400">Gender</div><div className="font-medium capitalize">{(data.profile.gender || "").replace(/_/g, " ")}</div></div>
                                        <div><div className="text-[11px] uppercase text-ink-400">Nationality</div><div className="font-medium">{data.profile.nationality}</div></div>
                                        <div className="sm:col-span-2"><div className="text-[11px] uppercase text-ink-400">Address</div><div>{data.profile.address}</div></div>
                                        <div><div className="text-[11px] uppercase text-ink-400">City / State / Pincode</div><div>{[data.profile.city, data.profile.state, data.profile.pincode].filter(Boolean).join(", ")}</div></div>
                                        <div className="sm:col-span-2"><div className="text-[11px] uppercase text-ink-400">Emergency contact</div><div>{data.profile.emergency_contact_name} · <span className="font-mono">{data.profile.emergency_contact_mobile}</span></div></div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] uppercase text-ink-400">Medical history</div>
                                        <div className="bg-paper-alt rounded-xl px-3 py-2 mt-1 whitespace-pre-line">{data.profile.medical_history}</div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <div><div className="text-[11px] uppercase text-ink-400">Pregnant</div><div className="capitalize">{(data.profile.is_pregnant || "not_applicable").replace(/_/g, " ")}</div></div>
                                        <div><div className="text-[11px] uppercase text-ink-400">Medications</div><div className="capitalize">{data.profile.medications}{data.profile.medications === "yes" && data.profile.medications_details ? ` — ${data.profile.medications_details}` : ""}</div></div>
                                    </div>
                                    <div className="text-[11px] text-ink-400 pt-2 border-t border-gray-100">
                                        Terms accepted on {data.profile.terms_accepted_at ? new Date(data.profile.terms_accepted_at).toLocaleString() : "—"} · Last updated {data.profile.updated_at ? new Date(data.profile.updated_at).toLocaleString() : "—"}
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-2xl bg-paper-alt border border-dashed border-gray-200 p-4 text-sm text-ink-700" data-testid="customer-profile-empty">
                                    This customer hasn't filled their enrollment profile yet.
                                </div>
                            )}
                            {(data.profile_history?.length || 0) > 0 && (
                                <div className="mt-3" data-testid="customer-profile-history">
                                    <h5 className="text-xs uppercase tracking-wider text-ink-400 font-semibold mb-2">Recent profile changes (last {Math.min(2, data.profile_history.length)})</h5>
                                    <div className="space-y-2">
                                        {data.profile_history.slice(0, 2).map((h, idx) => {
                                            const prev = h.previous || {};
                                            const curr = data.profile || {};
                                            return (
                                                <div key={idx} className="rounded-2xl bg-white border border-gray-100 p-3.5 text-sm" data-testid={`customer-profile-history-${idx}`}>
                                                    <div className="flex items-center justify-between gap-2 mb-2">
                                                        <div className="text-[11px] text-ink-400">
                                                            Changed on {h.snapshot_at ? new Date(h.snapshot_at).toLocaleString() : "—"}
                                                        </div>
                                                        <div className="text-[10px] uppercase tracking-wider bg-accent-50 text-accent-700 rounded-full px-2 py-0.5 font-semibold">
                                                            {(h.changed_keys || []).length} field{(h.changed_keys || []).length === 1 ? "" : "s"}
                                                        </div>
                                                    </div>
                                                    {(h.changed_keys || []).length === 0 ? (
                                                        <div className="text-xs text-ink-700">No tracked field changes recorded.</div>
                                                    ) : (
                                                        <ul className="space-y-1.5">
                                                            {(h.changed_keys || []).map((k) => (
                                                                <li key={k} className="grid sm:grid-cols-[140px_1fr] gap-2 text-xs">
                                                                    <span className="text-ink-700 font-medium capitalize">{k.replace(/_/g, " ")}</span>
                                                                    <span>
                                                                        <span className="bg-accent-50 text-accent-700 rounded px-1.5 py-0.5 line-through mr-1">
                                                                            {(prev[k] === null || prev[k] === undefined || prev[k] === "") ? "—" : String(prev[k])}
                                                                        </span>
                                                                        <span className="text-ink-400 mx-1">→</span>
                                                                        <span className="bg-brand-50 text-brand-700 rounded px-1.5 py-0.5 font-medium">
                                                                            {(curr[k] === null || curr[k] === undefined || curr[k] === "") ? "—" : String(curr[k])}
                                                                        </span>
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        <EnrollmentsAndFeedback userId={userId} orders={data.orders} />
                        <div>
                            <h4 className="font-semibold mb-2">Order history</h4>
                            {data.orders.length === 0 ? (
                                <div className="text-sm text-ink-700">No orders yet.</div>
                            ) : (
                                <div className="space-y-2">
                                    {data.orders.map((o) => (
                                        <div key={o.id} className="rounded-xl bg-white border border-gray-100 p-3 flex justify-between items-center text-sm">
                                            <div className="min-w-0">
                                                <div className="font-mono text-xs text-ink-700">#{o.id.slice(0, 8)}</div>
                                                <div className="text-xs text-ink-700">{String(o.created_at).slice(0, 10)} · {o.items?.length || 0} item(s)</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-semibold">{fmtINR(o.total ?? o.subtotal)}</div>
                                                <div className="text-[11px] uppercase tracking-wide text-ink-700">{o.status}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button onClick={() => onOpenChange(false)} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white">Close</Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function CustomersTab() {
    const [items, setItems] = useState([]);
    const [query, setQuery] = useState("");
    const [openId, setOpenId] = useState(null);

    const reload = () => api.get("/admin/customers").then(({ data }) => setItems(data));
    useEffect(() => { reload(); }, []);

    const filtered = useMemo(() => {
        const q = query.toLowerCase().trim();
        if (!q) return items;
        return items.filter((u) => (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q));
    }, [items, query]);

    return (
        <div className="space-y-4" data-testid="customers-tab">
            <div className="flex flex-wrap justify-between items-center gap-3">
                <div>
                    <h3 className="font-heading text-2xl">Customers</h3>
                    <p className="text-sm text-ink-700">{items.length} total · sorted newest first.</p>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                    <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or email" className="pl-9 rounded-full w-72" data-testid="customer-search" />
                </div>
            </div>
            <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-paper-alt text-left text-xs uppercase tracking-wide text-ink-700">
                        <tr>
                            <th className="px-4 py-3">Customer</th>
                            <th className="px-4 py-3">Role</th>
                            <th className="px-4 py-3 text-right">Orders</th>
                            <th className="px-4 py-3 text-right">Lifetime</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((u) => (
                            <tr key={u.id} className="border-t border-gray-100 hover:bg-paper-alt cursor-pointer" onClick={() => setOpenId(u.id)} data-testid={`customer-row-${u.id}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <div className="h-8 w-8 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center"><User className="h-4 w-4" /></div>
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">{u.name || "—"}</div>
                                            <div className="text-xs text-ink-700 truncate">{u.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`text-[11px] uppercase tracking-wide rounded-full px-2 py-0.5 ${u.role === "admin" ? "bg-accent-50 text-accent-700" : "bg-gray-100 text-ink-700"}`}>{u.role || "user"}</span>
                                    {u.is_suspended && <span className="ml-1.5 text-[11px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-red-50 text-red-700">suspended</span>}
                                </td>
                                <td className="px-4 py-3 text-right">{u.orders_count || 0}</td>
                                <td className="px-4 py-3 text-right font-medium">{fmtINR(u.lifetime_value)}</td>
                                <td className="px-4 py-3 text-right text-ink-700"><ChevronRight className="h-4 w-4 inline" /></td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={5} className="px-4 py-10 text-center text-ink-700">No customers found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <CustomerDetail open={Boolean(openId)} onOpenChange={(v) => !v && setOpenId(null)} userId={openId} onSaved={reload} />
        </div>
    );
}
